import {
    User,
    Plan,
    LensPlan,
    BillingCurrency,
} from "@/types/generated/management-api-types";
import { differenceInDays } from "date-fns";
type Organization = User["currentOrganization"];

export class OrganizationModel implements Organization {
    readonly id: string;
    readonly name?: string;
    readonly favicon?: string;
    readonly plan: Plan;
    readonly lensPlan?: LensPlan;
    // Typed off the wire shape rather than imported: TaggingPlan is not among
    // the types management-api-types.ts unpacks, and the terms fields below
    // already use this idiom.
    readonly taggingPlan?: Organization["taggingPlan"];
    // A downgrade or cancel waiting for the end of the paid period: when, and
    // the tier it moves to -- none for a cancel. taggingPlan stays the bought
    // tier until then, so these are the only sign that anything was asked for.
    readonly taggingPlanChangeAt?: string;
    readonly taggingPlanNext?: Organization["taggingPlanNext"];
    readonly isRegisteredCustomer: boolean;
    readonly hasPaymentMethod: boolean;
    readonly needsSubscriptionSetUp: boolean;
    readonly trialEndsAt?: string;
    // Answered by the Api (Organization::isInTrial), not derived here.
    // trialEndsAt alone cannot answer it: Stripe webhook reconciliation
    // re-populates that column with a converted subscription's historical
    // trial_end, and a payment method added mid-trial nulls it while Stripe
    // stays 'trialing'. Deriving a second answer in the app is how a screen
    // and the endpoint behind it come to disagree.
    readonly isInTrial: boolean;
    readonly cancelSubscriptionAt?: string;
    readonly lockedBillingCurrency?: BillingCurrency;
    readonly createdAt: string;
    readonly currentPeriodEnd?: string;
    readonly isPoc: boolean;
    readonly lensTrialCreditsClaimed: boolean;
    // Not sent to the app; required only by the generated type.
    readonly additionalWorkspaces: number;
    readonly additionalTaggingContainers: number;
    readonly acceptedTermsVersion?: Organization["acceptedTermsVersion"];
    readonly currentTermsVersion: Organization["currentTermsVersion"];
    readonly _isManager?: boolean;

    constructor({
        id,
        name,
        favicon,
        plan,
        lensPlan,
        taggingPlan,
        taggingPlanChangeAt,
        taggingPlanNext,
        isRegisteredCustomer,
        hasPaymentMethod,
        needsSubscriptionSetUp,
        trialEndsAt,
        isInTrial,
        cancelSubscriptionAt,
        lockedBillingCurrency,
        createdAt,
        currentPeriodEnd,
        isPoc,
        lensTrialCreditsClaimed,
        additionalWorkspaces,
        additionalTaggingContainers,
        acceptedTermsVersion,
        currentTermsVersion,
        isManager,
    }: Organization) {
        this.id = id;
        this.name = name;
        this.favicon = favicon;
        this.plan = plan;
        this.lensPlan = lensPlan;
        this.taggingPlan = taggingPlan;
        this.taggingPlanChangeAt = taggingPlanChangeAt ?? undefined;
        this.taggingPlanNext = taggingPlanNext ?? undefined;
        this.isRegisteredCustomer = isRegisteredCustomer;
        this.hasPaymentMethod = hasPaymentMethod;
        this.needsSubscriptionSetUp = needsSubscriptionSetUp;
        this.trialEndsAt = trialEndsAt;
        this.isInTrial = isInTrial;
        this.cancelSubscriptionAt = cancelSubscriptionAt;
        // The wire sends null for "not locked"; the app models absence as
        // undefined, the way every other optional here does.
        this.lockedBillingCurrency = lockedBillingCurrency ?? undefined;
        this.createdAt = createdAt;
        this.currentPeriodEnd = currentPeriodEnd;
        this.isPoc = isPoc ?? false;
        this.lensTrialCreditsClaimed = lensTrialCreditsClaimed ?? false;
        this.additionalWorkspaces = additionalWorkspaces ?? 0;
        this.additionalTaggingContainers = additionalTaggingContainers ?? 0;
        this.acceptedTermsVersion = acceptedTermsVersion;
        this.currentTermsVersion = currentTermsVersion;
        this._isManager = isManager;
    }

    static create(organizationData: Organization): OrganizationModel {
        return new this(organizationData);
    }

    public needsTermsAcceptance(): boolean {
        return this.acceptedTermsVersion !== this.currentTermsVersion;
    }

    public isFreePlan(): boolean {
        return ["free", "beta", "utm_scout", "audit"].includes(this.plan);
    }

    public hasLensPlan(): boolean {
        return !!this.lensPlan;
    }

    public hasTaggingPlan(): boolean {
        return !!this.taggingPlan;
    }

    /** A downgrade or cancel of the tagging tier is booked for the period's end. */
    public hasScheduledTaggingPlanChange(): boolean {
        return !!this.taggingPlanChangeAt;
    }

    /**
     * A server-side tracking tier granted rather than bought.
     *
     * `billing:set-tagging-free` is the only thing that sets it and
     * `POST /user/organization/subscription/tagging` refuses it, so this is
     * the one tier with an entitlement but no self-service actions behind it
     * -- a distinction LensPlan has no equivalent of.
     */
    public hasCompedTaggingPlan(): boolean {
        return this.taggingPlan === "free";
    }

    /**
     * A paid proof of concept, which shares trialEndsAt with a free trial and
     * must not be described like one.
     *
     * The window was bought outright, so there is nothing to convert and no
     * payment method to chase -- and the "Add payment method" call to action a
     * trial shows would open a Stripe setup session against an organization
     * that deliberately has no Stripe customer.
     */
    public isPocInWindow(): boolean {
        return this.isPoc && this.isInTrial;
    }

    public getTrialDaysLeft(): number {
        if (!this.trialEndsAt) {
            return 0;
        }

        const now = new Date();
        const trialEndsAt = new Date(this.trialEndsAt);

        return differenceInDays(trialEndsAt, now) + 1; // Difference in days only measures full days, so add 1 to get the number of days left
    }
}
